/**
 * API Configuration
 * Centralized configuration for backend API endpoints
 */

/**
 * API Configuration
 * Change this URL when switching from Cloud Run to custom domain
 */
export const API_CONFIG = {
    // Production: Custom domain (current)
    // BASE_URL: 'https://api.kassie.media',

    // Previous: Cloud Run URL
    // BASE_URL: 'https://kassie-backend-695227034984.africa-south1.run.app',

    // Local development (uncomment for local testing)
    BASE_URL: 'http://localhost:8000',

    TIMEOUT: 30000, // 30 seconds (first request after backend restart can be slow)
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000, // 1 second
};

/**
 * API Endpoints
 * All backend endpoints organized by feature
 */
export const API_ENDPOINTS = {
    // Health check
    HEALTH: '/api/health',

    // Authentication & Users
    AUTH: {
        DEVICE_GENERATE_CODE: '/api/auth/device/activation-code/generate',
        DEVICE_VALIDATE_CODE: '/api/auth/device/activation-code/validate',
        DEVICE_AUTHORIZE: '/api/auth/device/authorize',
        DEVICE_REVOKE: '/api/auth/device/revoke',
    },

    // Users
    USERS: {
        ME: '/api/users/me',
        REGISTER: '/api/users/register',
        UPDATE: '/api/users/me',
    },

    // Families
    FAMILIES: {
        LIST: '/api/families',
        CREATE: '/api/families',
        GET: (familyId) => `/api/families/${familyId}`,
        UPDATE: (familyId) => `/api/families/${familyId}`,
        DELETE: (familyId) => `/api/families/${familyId}`,
        INVITE: (familyId) => `/api/families/${familyId}/invite`,
        PROMOTE: (familyId, memberId) => `/api/families/${familyId}/members/${memberId}/promote`,
    },

    // Child Profiles
    PROFILES: {
        LIST: (familyId) => `/api/families/${familyId}/profiles`,
        CREATE: (familyId) => `/api/families/${familyId}/profiles`,
        GET: (familyId, profileId) => `/api/families/${familyId}/profiles/${profileId}`,
        UPDATE: (familyId, profileId) => `/api/families/${familyId}/profiles/${profileId}`,
        DELETE: (familyId, profileId) => `/api/families/${familyId}/profiles/${profileId}`,
        PROGRESS: (profileId) => `/api/profiles/${profileId}/progress`,
        STARS: (profileId) => `/api/profiles/${profileId}/stars`,
    },

    // Content Catalog
    CATALOG: {
        MODULES: '/api/catalog/modules',
        MODULE: (moduleId) => `/api/catalog/modules/${moduleId}`,
    },

    // Entitlements & Gifting
    ENTITLEMENTS: {
        LIST: (familyId) => `/api/families/${familyId}/entitlements`,
        GRANT: '/api/entitlements/grant',
        REVOKE: '/api/entitlements/revoke',
        GIFT: '/api/entitlements/gift',
    },

    // Tokens & Rewards
    TOKENS: {
        BALANCE: (userId) => `/api/users/${userId}/tokens`,
        GIFT: '/api/tokens/gift',
        SPEND: '/api/tokens/spend',
        TRANSACTIONS: (userId) => `/api/users/${userId}/token-transactions`,
    },

    // Messages
    MESSAGES: {
        SEND: '/api/messages',
        INBOX: (profileId) => `/api/profiles/${profileId}/inbox`,
        MARK_READ: (messageId) => `/api/messages/${messageId}/open`,
    },

    // Recordings
    RECORDINGS: {
        LIST: (familyId) => `/api/families/${familyId}/recordings`,
        UPLOAD: '/api/recordings/upload',
        DELETE: (recordingId) => `/api/recordings/${recordingId}`,
    },

    // CMS (Admin only)
    CMS: {
        MODULES: '/api/cms/modules',
        CREATE_MODULE: '/api/cms/modules',
        UPDATE_MODULE: (moduleId) => `/api/cms/modules/${moduleId}`,
        DELETE_MODULE: (moduleId) => `/api/cms/modules/${moduleId}`,
    },

    // Leads
    LEADS: {
        CREATE: '/api/leads',
        INVITE: (leadId) => `/api/leads/${leadId}/invite`,
    },
};

/**
 * Get full API URL
 * @param {string} endpoint - API endpoint path
 * @returns {string} Full URL
 */
export function getApiUrl(endpoint) {
    return `${API_CONFIG.BASE_URL}${endpoint}`;
}

/**
 * Environment detection
 */
export const IS_DEVELOPMENT = import.meta.env.DEV;
export const IS_PRODUCTION = import.meta.env.PROD;

/**
 * Child App URL Configuration
 */
export const CHILD_APP_CONFIG = {
    BASE_URL: import.meta.env.VITE_CHILD_APP_URL || 'http://localhost:3001'
};

/**
 * Quick URL switcher for different environments
 * Uses environment variable or falls back to localhost:8000 for development
 */
export function switchApiEnvironment() {
    // For development, always use localhost:8000 (backend port)
    if (IS_DEVELOPMENT) {
        API_CONFIG.BASE_URL = 'http://localhost:8000';
        CHILD_APP_CONFIG.BASE_URL = import.meta.env.VITE_CHILD_APP_URL || 'http://localhost:3001';
        console.log('🔗 API Base URL (development):', API_CONFIG.BASE_URL);
        console.log('🔗 Child App URL (development):', CHILD_APP_CONFIG.BASE_URL);
        return;
    }

    // Use environment variable if available (production)
    if (import.meta.env.VITE_API_URL) {
        API_CONFIG.BASE_URL = import.meta.env.VITE_API_URL;
        console.log('🔗 API Base URL (from env):', API_CONFIG.BASE_URL);
    }
    // Otherwise use Cloud Run URL (production fallback)
    else {
        API_CONFIG.BASE_URL = 'https://kassie-backend-695227034984.africa-south1.run.app';
        console.log('🔗 API Base URL (fallback):', API_CONFIG.BASE_URL);
    }

    // Set child app URL for production
    if (import.meta.env.VITE_CHILD_APP_URL) {
        CHILD_APP_CONFIG.BASE_URL = import.meta.env.VITE_CHILD_APP_URL;
        console.log('🔗 Child App URL (from env):', CHILD_APP_CONFIG.BASE_URL);
    } else {
        CHILD_APP_CONFIG.BASE_URL = 'https://app.kassie.media';
        console.log('🔗 Child App URL (fallback):', CHILD_APP_CONFIG.BASE_URL);
    }
}

// Initialize on import
switchApiEnvironment();
